import { useEffect, useRef, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Send, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const AGENT_INTRO = {
  concierge: "Hi! I'm your Concierge. Ask me about amenities, rules, payments, or anything about the community.",
  maintenance: "Describe a maintenance issue and I'll help you triage it — priority, category, and next steps.",
  arc: "Tell me about the exterior change you want to make and I'll flag likely committee concerns.",
  payment_advisor: "Ask about your balance, payment plans, or how late fees & aging work.",
  violation_precheck: "Describe what you're planning (party, painting, parking, etc.) — I'll check for likely violations.",
};

export default function AIConciergeDrawer({ agent = "concierge", theme = "dark" }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/ai/history?agent=${agent}`);
        setMessages(data.messages || []);
      } catch (err) { if (typeof console !== 'undefined') console.debug('components/AIConciergeDrawer.jsx: swallowed', err); }
    })();
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [agent]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streamingText, sending]);

  const streamReply = async (msg) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const controller = new AbortController();
    abortRef.current = controller;
    const res = await fetch(`${base}/api/ai/chat-stream`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ agent, message: msg }),
      signal: controller.signal,
    });
    if (!res.ok || !res.body) {
      throw new Error(`chat-stream ${res.status}`);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let fullText = "";
    let done = false;
    while (!done) {
      const { value, done: streamDone } = await reader.read();
      done = streamDone;
      if (value) buffer += decoder.decode(value, { stream: true });
      // Parse SSE frames: separated by blank lines
      let idx;
      while ((idx = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const evLine = frame.split("\n").find((l) => l.startsWith("event:")) || "";
        const dataLine = frame.split("\n").find((l) => l.startsWith("data:")) || "";
        const ev = evLine.replace("event:", "").trim();
        let data = {};
        try { data = JSON.parse(dataLine.replace("data:", "").trim() || "{}"); } catch (err) { if (typeof console !== 'undefined') console.debug('components/AIConciergeDrawer.jsx: swallowed', err); }
        if (ev === "delta" && data.text) {
          fullText += data.text;
          setStreamingText(fullText);
        } else if (ev === "done") {
          setMessages((m) => [...m, {
            role: "assistant", content: fullText,
            at: data.at || new Date().toISOString(),
            sources: data.sources || [],
          }]);
          setStreamingText("");
        } else if (ev === "error") {
          throw new Error(data.error || "stream error");
        }
      }
    }
  };

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setInput("");
    setSending(true);
    setStreamingText("");
    const nowIso = new Date().toISOString();
    setMessages((m) => [...m, { role: "user", content: msg, at: nowIso }]);
    try {
      await streamReply(msg);
    } catch (e) {
      // Fall back to the classic /chat endpoint if streaming is unavailable.
      try {
        const { data } = await api.post("/ai/chat", { agent, message: msg });
        setMessages((m) => [...m, {
          role: "assistant", content: data.reply, at: data.at,
          sources: data.sources || [],
        }]);
      } catch (e2) {
        toast.error(apiErrorMessage(e2));
      }
    } finally {
      setSending(false);
      setStreamingText("");
    }
  };

  const isDark = theme === "dark";

  return (
    <div className="flex flex-col h-full min-h-0">
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto p-4 scroll-slim">
        <div className={`rounded-lg p-3 mb-3 text-sm ${isDark ? "bg-indigo-500/10 border border-indigo-500/30 text-indigo-100" : "bg-indigo-50 border border-indigo-100 text-indigo-900"}`}>
          <div className="flex items-center gap-2 font-medium mb-1">
            <Sparkles className="h-4 w-4" /> Claude Sonnet 4.5 · streaming
          </div>
          {AGENT_INTRO[agent] || "How can I help?"}
        </div>
        {messages.map((m, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-3 flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              data-testid={`ai-msg-${m.role}`}
              className={
                "max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed " +
                (m.role === "user"
                  ? isDark
                    ? "bg-orange-500 text-white"
                    : "bg-zinc-900 text-white"
                  : isDark
                    ? "bg-white/[0.06] text-zinc-100 border border-white/10"
                    : "bg-white text-zinc-900 border border-black/5 shadow-sm")
              }
            >
              {m.content}
              {(m.sources || []).length > 0 && (
                <div className={"mt-2 pt-2 border-t " + (isDark ? "border-white/10" : "border-black/5")}>
                  <div className={"text-[10px] uppercase tracking-widest mb-1 " + (isDark ? "text-zinc-400" : "text-zinc-500")}>
                    Sources
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {m.sources.map((s) => (
                      <span
                        key={s.id}
                        data-testid={`ai-source-${s.id}`}
                        className={
                          "inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full " +
                          (isDark
                            ? "bg-orange-500/10 border border-orange-500/30 text-orange-300"
                            : "bg-orange-50 border border-orange-200 text-orange-700")
                        }
                        title={s.category}
                      >
                        📎 {s.citation_key || s.title}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        ))}
        {streamingText && (
          <motion.div initial={{ opacity: 0.6 }} animate={{ opacity: 1 }} className="mb-3 flex justify-start">
            <div
              data-testid="ai-msg-streaming"
              className={
                "max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed " +
                (isDark ? "bg-white/[0.06] text-zinc-100 border border-white/10"
                        : "bg-white text-zinc-900 border border-black/5 shadow-sm")
              }
            >
              {streamingText}
              <span className={"ml-1 inline-block align-baseline w-1.5 h-4 " + (isDark ? "bg-indigo-300" : "bg-indigo-500") + " animate-pulse"} />
            </div>
          </motion.div>
        )}
        {sending && !streamingText && (
          <div className={`text-xs ${isDark ? "text-zinc-400" : "text-zinc-500"} animate-pulse`}>Thinking…</div>
        )}
      </div>
      <div className={`p-3 border-t ${isDark ? "border-white/10" : "border-black/5"}`}>
        <div className="flex gap-2">
          <Input
            data-testid="ai-input"
            placeholder="Type a message…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") send(); }}
            className={isDark ? "bg-white/[0.04] border-white/10 text-white placeholder:text-zinc-500" : ""}
          />
          <Button data-testid="ai-send" onClick={send} disabled={sending}>
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
